import ewAnalytics from 'utils/analytics';
import { getShippingMethodId, getShippingMethodInfo } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { FormattedProductForTracking } from 'utils/types';
import { LineItem, Order, CheckoutData } from './types';

export const formatLineItemsForCheckout = (lineItems: LineItem[]) => {
  return lineItems.map((product) => {
    return {
      name: product.product_name,
      id: product.product_id,
      price: parseFloat(product.base_price),
      variant: product.variant_sku,
      quantity: product.quantity,
      is_subscription: product.is_subscription,
      subscription_frequency: product.subscription_frequency,
    };
  });
};

export const formatSkus = (lineItems: LineItem[]): string[] => {
  return lineItems.map(function (line_item) {
    return line_item.variant_sku;
  });
};

export const getCoupon = (order: Order) => {
  return (
    order.promotions && order.promotions.length > 0 && order.promotions[0].code
  );
};

export const dataLayerCheckout = (order: Order): void => {
  const products = formatLineItemsForCheckout(order.line_items);

  window &&
    window.dataLayer.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            // this step refers to a point in the checkout funnel
            step: 2,
          },
          products,
        },
      },
      userEmail: order.email,
      userId: order.user_id,
    });
};

export const dataLayerReceiveMarketing = (receiveMarketing: boolean): void => {
  window &&
    window.dataLayer.push({
      event: 'marketing',
      marketing: {
        receiveMarketing,
      },
    });
};

export const dataLayerPurchase = (order: Order): void => {
  const coupon = getCoupon(order);
  const products = formatLineItemsForCheckout(order.line_items);

  window &&
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: order.id,
            revenue: parseFloat(order.total),
            coupon,
            discount: parseFloat(order.promo_total),
          },
          products,
        },
      },
      userEmail: order.email,
      userId: order.user_id,
    });
};

export const dataLayerCaptureUserAttributes = (
  formData: CheckoutData,
): void => {
  const { ship_address, email } = formData;
  const { phone, state, city, first_name, last_name } = ship_address;
  window &&
    window.dataLayer.push({
      event: 'cartCheckout',
      marketing: {
        attributes: {
          phone,
          state,
          city,
          first_name,
          last_name,
          email,
        },
      },
    });
};

export const dataLayerCartAbandonment = (email: string): void => {
  window &&
    window.dataLayer.push({
      event: 'cartAbandonment',
      marketing: {
        cartAbandonment: email,
      },
    });
};

export const mixpanelPurchase = (order: Order): void => {
  const coupon = getCoupon(order);
  const products = formatLineItemsForCheckout(order.line_items);
  const variant_skus = formatSkus(order.line_items);
  const shippingMethodId = getShippingMethodId();
  const { shippingType, shippingCost } =
    getShippingMethodInfo(shippingMethodId);

  ewAnalytics.track({
    event: ANALYTICS.EVENTS.PURCHASE,
    data: {
      id: order.id,
      revenue: parseFloat(order.total),
      coupon,
      discount: parseFloat(order.promo_total),
      products,
      variant_skus,
      userId: order.user_id,
      shipping_method_id: shippingMethodId,
      shipping_type: shippingType,
      shipping_cost: shippingCost,
    },
  });
};

export const mixpanelValidationErrors = (
  errorMessages: string[],
  errorFields?: string[],
): void => {
  ewAnalytics.track({
    event: ANALYTICS.EVENTS.CHECKOUT_VALIDATION_ERROR,
    data: {
      Page: 'Checkout',
      Errors: errorMessages,
      Fields: errorFields,
    },
  });
};

export const mixpanelPurchaseError = (
  failedProcessingOrder: boolean,
  errors: string[],
): void => {
  const event = failedProcessingOrder
    ? ANALYTICS.EVENTS.CHECKOUT_PROCESSING_ERROR
    : ANALYTICS.EVENTS.CHECKOUT_COMPLETING_ERROR;
  ewAnalytics.track({
    event,
    data: {
      synchronous: true,
      errors,
    },
  });
};

export const executeVwoGoalConversion = (goal: number) => {
  window._vis_opt_queue = (window && window._vis_opt_queue) || [];
  window._vis_opt_queue.push(function () {
    // @ts-ignore
    _vis_opt_goal_conversion(goal);
  });
};

export const logFormInteraction = (component: string) => {
  ewAnalytics.track({
    event: ANALYTICS.EVENTS.FORM_INTERACTION,
    data: {
      component,
    },
  });
};
